import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/errors/use-toast';
import { updatePageContent as updatePageContentOperation, UpdatePageContentInput, deletePageContent } from '@/hooks/content-generator/pages/page-content-operations';
import { pageContentKeys } from '@/hooks/content-generator/pages/use-page-content-hooks';

interface UseUniversalPageMutationsOptions {
  pageId: string;
  workbookId?: string; // undefined/null for private pages, project ID for project pages
  onSuccess?: (updatedPage: any) => void;
  onError?: (error: any) => void;
}

/**
 * Universal page mutations hook that works for both project and private pages
 * Handles smart cache invalidation based on page type
 */
export const useUniversalPageMutations = ({ 
  pageId, 
  workbookId, 
  onSuccess, 
  onError 
}: UseUniversalPageMutationsOptions) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updatePageMutation = useMutation({
    mutationFn: (updates: UpdatePageContentInput) => {
      // Defensive check to prevent empty pageId calls
      if (!pageId || pageId.trim() === '') {
        console.warn('[useUniversalPageMutations] Attempted to update page with empty pageId - aborting');
        return Promise.reject(new Error('Cannot update page: pageId is empty'));
      }
      return updatePageContentOperation(pageId, updates);
    },
    onSuccess: (updatedPage) => {
      // Always invalidate the specific page detail
      queryClient.invalidateQueries({ queryKey: pageContentKeys.detail(pageId) });
      
      if (workbookId) {
        // Project page - invalidate project-specific caches
        queryClient.invalidateQueries({ queryKey: pageContentKeys.byWorkbook(workbookId) });
        queryClient.invalidateQueries({ queryKey: ['project-navigation', workbookId] });
      } else {
        // Private page - invalidate private page cache
        queryClient.invalidateQueries({ queryKey: ['private-pages'] });
      }

      // Call custom success handler if provided
      onSuccess?.(updatedPage);
    },
    onError: (error) => {
      console.error('Error updating page:', error);
      toast({
        title: "Error",
        description: "Failed to update page",
        variant: "destructive",
      });
      
      // Call custom error handler if provided
      onError?.(error);
    },
  });

  const deletePageMutation = useMutation({
    mutationFn: () => deletePageContent(pageId),
    onSuccess: () => {
      // Remove the specific page detail from cache
      queryClient.removeQueries({ queryKey: pageContentKeys.detail(pageId) });
      
      if (workbookId) {
        // Project page - invalidate project-specific caches
        queryClient.invalidateQueries({ queryKey: pageContentKeys.byWorkbook(workbookId) });
        queryClient.invalidateQueries({ queryKey: ['project-navigation', workbookId] });
      } else {
        // Private page - invalidate private page cache
        queryClient.invalidateQueries({ queryKey: ['private-pages'] });
      }

      toast({
        title: "Page Deleted",
        description: "The page has been deleted successfully.",
        variant: "success",
      });

      // Call custom success handler if provided
      onSuccess?.(null);
    },
    onError: (error) => {
      console.error('Error deleting page:', error);
      toast({
        title: "Error",
        description: "Failed to delete page",
        variant: "destructive",
      });
      
      // Call custom error handler if provided
      onError?.(error);
    },
  });

  const updatePageTitle = async (title: string) => {
    return updatePageMutation.mutateAsync({ title });
  };

  const updatePageContent = async (content: string) => {
    return updatePageMutation.mutateAsync({ content });
  };

  const updatePageMetadata = async (updates: Partial<UpdatePageContentInput>) => {
    return updatePageMutation.mutateAsync(updates);
  };

  const deletePage = async () => {
    return deletePageMutation.mutateAsync();
  };

  return {
    updatePage: updatePageMutation.mutate,
    updatePageAsync: updatePageMutation.mutateAsync,
    updatePageTitle,
    updatePageContent,
    updatePageMetadata,
    deletePage,
    deletePageAsync: deletePageMutation.mutateAsync,
    isPending: updatePageMutation.isPending || deletePageMutation.isPending,
    isError: updatePageMutation.isError || deletePageMutation.isError,
    error: updatePageMutation.error || deletePageMutation.error,
  };
}; 